import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  taxInvoicesOutgoing,
  type TaxInvoiceOutgoingListItem,
} from '@/api/taxInvoices';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function TaxInvoicesOutgoingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Стандартні фільтри списків (2026-08-07, зауваження власника — як
  // /customer-orders): орг слідує за шапкою; локальні дати ПОРОЖНІ —
  // діє глобальний «період за замовчуванням» з шапки.
  const f = useDocListFilters();
  const [kindFilter, setKindFilter] = useState<'ALL' | 'PN' | 'RK'>('ALL');
  // Колонка «Організація» — map id→name (2026-08-22).
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);
  // 2026-08-09 (Норма #29): серверна пагінація + сортування; фільтр
  // ПН/РК тепер СЕРВЕРНИЙ (is_correction) — раніше клієнтський по 200.
  const { datasource, total } = useInfiniteGridDatasource<TaxInvoiceOutgoingListItem>(
    (limit, offset, sort) => taxInvoicesOutgoing.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom?.slice(0, 10),
      date_to: f.dateTo?.slice(0, 10),
      is_correction: kindFilter === 'ALL' ? undefined : kindFilter === 'RK',
    }),
    [kindFilter, ...f.queryKey],
    { attachmentsType: 'tax.tax_invoices_outgoing' },
  );

  const cols = useMemo<ColDef<TaxInvoiceOutgoingListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: TaxInvoiceOutgoingListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    {
      headerName: 'Вид', width: 70, sortable: false,
      valueGetter: (p) => p.data
        ? ((p.data as { is_correction?: boolean }).is_correction ? 'РК' : 'ПН') : '',
    },
    { field: 'invoice_number', headerName: '№', width: 130, sortable: true },
    {
      field: 'invoice_date', headerName: t('cd.date'), width: 130, sortable: true,
      valueFormatter: (p) => p.value
        ? new Date(p.value).toLocaleDateString(i18n.language) : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 200, sortable: true,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
    { field: 'contractor_name', headerName: t('gs.contractor'), flex: 1, minWidth: 200, sortable: false },
    {
      field: 'total_amount', headerName: t('cd.amount'), width: 130, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      field: 'erpn_status', headerName: 'ЄРПН', width: 150, sortable: false,
      cellRenderer: (p: { data?: TaxInvoiceOutgoingListItem }) => {
        const r = p.data;
        if (!r) return '';
        const m: Record<string, [string, string]> = {
          NOT_SENT: ['не відправлено', '#64748b'],
          DRAFT: ['чернетка подачі', '#64748b'],
          SENDING: ['відправляється', '#1d4ed8'],
          SENT: ['обробляється', '#1d4ed8'],
          REGISTERED: ['зареєстровано', '#15803d'],
          STOPPED: ['зупинено', '#b45309'],
          BLOCKED: ['заблоковано', '#b45309'],
          REJECTED: ['відмовлено', '#dc2626'],
          ERROR: ['помилка', '#dc2626'],
        };
        const [txt, color] = m[r.erpn_status] ?? [r.erpn_status, '#334155'];
        const title = [
          r.erpn_registration_date
            ? `Зареєстровано ${new Date(r.erpn_registration_date).toLocaleDateString('uk-UA')}`
            : null,
          r.erpn_response ?? null,
        ].filter(Boolean).join('\n') || undefined;
        return (
          <span style={{ color, fontWeight: r.erpn_status === 'REGISTERED' ? 600 : 400,
            cursor: title ? 'help' : undefined }}
            title={title}>
            {txt}
          </span>
        );
      },
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [t, i18n.language, orgName]);

  // UX β: фільтр org dropdown по tax.invoice.manage permission
  const {
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('tax.invoice.manage');

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>ПН/РК (вихідні)<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/tax-invoices-outgoing/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати податкові накладні у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Нова ПН
        </button>
      </div>
      <DocListFiltersRow>
        <select value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as 'ALL' | 'PN' | 'RK')}
          style={{ width: 110, flex: '0 0 auto' }}>
          <option value="ALL">Вид: усі</option>
          <option value="PN">ПН</option>
          <option value="RK">РК</option>
        </select>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<TaxInvoiceOutgoingListItem>
        deleteRow={{ remove: (id) => taxInvoicesOutgoing.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="tax-invoices-outgoing"
        onRowDoubleClicked={(r) => navigate(`/tax-invoices-outgoing/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />

    </div>
  );
}
